// ============================================================
// parseComposer (v8.363.1) - what the line in the box means.
//
//   /w <name> <text>    a private vox to a person (by display name, or by
//                       the name of the combatant they act as)
//   /t <group> <text>   a group channel by name, or "players"
//   /me <text>          an emote
//   /roll <expr>        dice, validated here, rolled by the caller
//   /ic  /ooc           speak as the acting combatant / out of character
//   anything else       say, to the active conversation
//
// Names resolve case-insensitively: an exact match wins, else a prefix of
// the name or of any word in it ("/t ares" finds Kill-team Ares, "/w cain"
// finds whoever acts as Brother Cain); an ambiguous prefix is an error
// that names the candidates. Pure.
// ============================================================
import { parseDiceExpression } from '../dice';
import type { ChatChannel, Person } from './types';

export type ParsedLine =
  | { kind: 'say'; text: string }
  | { kind: 'emote'; text: string }
  | { kind: 'roll'; expr: string }
  | { kind: 'whisper'; uid: string; text: string }
  | { kind: 'group'; channelId: string | 'players'; text: string }
  /** The GM's announcement to the table (v9.7.0): a card in the log, not a line of talk. */
  | { kind: 'announce'; text: string }
  | { kind: 'ic' }
  | { kind: 'ooc' }
  | { kind: 'error'; message: string };

export interface ParseContext {
  people: Person[];
  channels: ChatChannel[];
  me: string;
}

/** `q` starts the name, or any word of it. */
function wordPrefix(name: string, q: string): boolean {
  return name.startsWith(q) || name.split(/\s+/).some((w) => w.startsWith(q));
}

/** The people a name could mean - exact (case-insensitive) first, then a prefix of the name or of a word in it. */
export function resolvePerson(name: string, people: Person[], me: string): Person[] {
  const q = name.trim().toLowerCase();
  if (!q) return [];
  const others = people.filter((p) => p.uid !== me);
  const names = (p: Person) => [p.name, p.actingAsName ?? ''].filter(Boolean).map((n) => n.toLowerCase());
  const exact = others.filter((p) => names(p).some((n) => n === q));
  if (exact.length) return exact;
  return others.filter((p) => names(p).some((n) => wordPrefix(n, q)));
}

function resolveChannel(name: string, channels: ChatChannel[]): (ChatChannel | 'players')[] {
  const q = name.trim().toLowerCase();
  if (!q) return [];
  const all: (ChatChannel | 'players')[] = ['players', ...channels];
  const label = (c: ChatChannel | 'players') => (c === 'players' ? 'players' : c.name.toLowerCase());
  const exact = all.filter((c) => label(c) === q);
  if (exact.length) return exact;
  return all.filter((c) => wordPrefix(label(c), q));
}

/**
 * Split "<target> <text>" where the target may be several words: the
 * longest leading word-run that resolves to exactly one thing wins, so
 * "/w Brother Cain hold the line" finds Brother Cain.
 */
function splitTarget<T>(rest: string, resolve: (name: string) => T[]): { target: T; text: string } | { error: string } {
  const words = rest.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return { error: 'Say who.' };
  let ambiguous: T[] | null = null;
  for (let n = Math.min(words.length, 4); n >= 1; n--) {
    const name = words.slice(0, n).join(' ');
    const found = resolve(name);
    if (found.length === 1) return { target: found[0], text: words.slice(n).join(' ') };
    if (found.length > 1 && !ambiguous) ambiguous = found;
  }
  if (ambiguous) return { error: `Which one: ${ambiguous.map(describe).join(', ')}?` };
  return { error: `Nobody called "${words[0]}" is at the table.` };
}

function describe(t: unknown): string {
  if (t === 'players') return 'Players';
  const o = t as { name?: string };
  return o?.name ?? String(t);
}

export function parseComposer(raw: string, ctx: ParseContext): ParsedLine {
  const line = raw.trim();
  if (!line) return { kind: 'error', message: 'Nothing to say.' };
  const m = line.startsWith('/') ? line.match(/^\/(\w+)\s*([\s\S]*)$/) : null;
  if (!m) return { kind: 'say', text: line };
  const [, cmd, rest] = m;
  const command = COMMANDS.get(cmd.toLowerCase());
  if (!command)
    return { kind: 'error', message: `Unknown command /${cmd}. Try /w, /t, /me, /roll, /ic, /ooc, /announce.` };
  return command(rest, ctx);
}

/** One slash command: what follows it, read against the table. */
type Command = (rest: string, ctx: ParseContext) => ParsedLine;

function whisper(rest: string, ctx: ParseContext): ParsedLine {
  const r = splitTarget(rest, (name) => resolvePerson(name, ctx.people, ctx.me));
  if ('error' in r) return { kind: 'error', message: r.error };
  if (!r.text) return { kind: 'error', message: `Say what to ${r.target.name}.` };
  return { kind: 'whisper', uid: r.target.uid, text: r.text };
}

function group(rest: string, ctx: ParseContext): ParsedLine {
  const r = splitTarget(rest, (name) => resolveChannel(name, ctx.channels));
  if ('error' in r) {
    return {
      kind: 'error',
      message: r.error.replace('Nobody called', 'No vox group called').replace('is at the table', 'exists'),
    };
  }
  const channelId = r.target === 'players' ? 'players' : r.target.id;
  if (!r.text) return { kind: 'error', message: 'Say what to the group.' };
  return { kind: 'group', channelId, text: r.text };
}

function emote(rest: string): ParsedLine {
  if (!rest.trim()) return { kind: 'error', message: 'Do what?' };
  return { kind: 'emote', text: rest.trim() };
}

function announce(rest: string): ParsedLine {
  if (!rest.trim()) return { kind: 'error', message: 'Announce what?' };
  return { kind: 'announce', text: rest.trim() };
}

function roll(rest: string): ParsedLine {
  const expr = rest.trim();
  if (!expr || parseDiceExpression(expr).length === 0)
    return { kind: 'error', message: `"${expr}" is not a dice expression (try 2d10+3).` };
  return { kind: 'roll', expr };
}

/** A Map, not an object literal, so `/constructor` is unknown rather than Object's own. */
const COMMANDS = new Map<string, Command>([
  ['w', whisper],
  ['whisper', whisper],
  ['t', group],
  ['team', group],
  ['group', group],
  ['me', emote],
  ['announce', announce],
  ['roll', roll],
  ['r', roll],
  ['ic', () => ({ kind: 'ic' })],
  ['ooc', () => ({ kind: 'ooc' })],
]);
